import { useEffect, useRef } from 'react';

function JobTracker({ jobId, userId, apiBaseUrl, onComplete, onError, onCancel, onStatusUpdate }) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const jobCompletedRef = useRef(false);
  const previousJobIdRef = useRef(null);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Store callbacks in refs to avoid re-triggering the WebSocket effect
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onCancelRef = useRef(onCancel);
  onStatusUpdateRef.current = onStatusUpdate;
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!jobId) return;

    if (previousJobIdRef.current !== jobId) {
      jobCompletedRef.current = false;
      previousJobIdRef.current = jobId;
    }

    const connectWebSocket = () => {
      try {
        if (jobCompletedRef.current) return;

        let wsUrl;
        if (apiBaseUrl && apiBaseUrl.startsWith('http')) {
          const url = new URL(apiBaseUrl);
          const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
          wsUrl = `${wsProtocol}//${url.host}/ws/job/${jobId}`;
        } else {
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const host = window.location.host;
          wsUrl = `${protocol}//${host}/api/ws/job/${jobId}`;
        }

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'ping') return;
            if (!data.status) return;

            if (onStatusUpdateRef.current) {
              onStatusUpdateRef.current(data);
            }

            if (data.status === 'success' || data.status === 'failed' || data.status === 'cancelled') {
              jobCompletedRef.current = true;

              if (data.status === 'success' && onCompleteRef.current) onCompleteRef.current(data);
              if (data.status === 'failed' && onErrorRef.current) onErrorRef.current(data);
              if (data.status === 'cancelled' && onCancelRef.current) onCancelRef.current(data);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          if (jobCompletedRef.current) return;
          console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
          if (jobCompletedRef.current) return;

          if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttemptsRef.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, delay);
          }
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
      }
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close(1000, 'Component unmounting');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Headless component — no UI rendering
  return null;
}

export default JobTracker;
